import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { formatFechaLima } from '../../utils/date';
import {
  leccionesAprendidasAPI,
  proyectosAPI,
  parametrosAPI,
  subdivisionesAPI,
} from '../../utils/api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Trash2,
  Plus,
  Loader2,
  Edit,
  CheckCircle2,
  Eye,
  BookOpen,
  AlertTriangle,
  Paperclip,
  FileText,
  FileImage,
  Download,
  X,
} from 'lucide-react';

const fmt = (s) => {
  if (!s) return '—';
  return formatFechaLima(s, { day: '2-digit', month: 'long', year: 'numeric' }) || s;
};

const REQ_TEXT_FIELDS = [
  'responsable_nombre',
  'etapa_deteccion',
  'especialidad',
  'lugar_ejes',
  'descripcion_situacion',
  'problema_identificado',
  'causa',
  'recomendacion',
];

const blankForm = () => ({
  proyecto_id: '',
  aplica_a_todas: false,
  etapa_codigo: null,
  fecha_identificacion: new Date().toISOString().slice(0, 10),
  responsable_nombre: '',
  responsable_user_id: null,
  etapa_deteccion: '',
  especialidad: '',
  lugar_ejes: '',
  descripcion_situacion: '',
  problema_identificado: '',
  causa: '',
  recomendacion: '',
  narrativa_impactos: '',
  acciones: [{ descripcion: '' }],
});

const LeccionDetalle = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isNew = !id || id === 'nuevo';
  const editParam = searchParams.get('edit') === '1';

  // Base path = pathname actual sin el segmento `nuevo` o el `:id`.
  // Permite preservar el contexto de URL (corta o calificada
  // /constructora/calidad/...) al volver a la lista o al detalle creado.
  const basePath = useMemo(() => {
    const p = location.pathname.replace(/\/$/, '');
    // Estrip `/nuevo` o `/<id>` (último segmento) si aplica
    const idx = p.lastIndexOf('/');
    if (idx > 0) {
      const last = p.slice(idx + 1);
      if (last === 'nuevo' || (id && last === id)) {
        return p.slice(0, idx);
      }
    }
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, id]);

  const [mode, setMode] = useState(isNew ? 'edit' : 'view');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [marking, setMarking] = useState(false);

  const [proyectos, setProyectos] = useState([]);
  const [etapasDeteccion, setEtapasDeteccion] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [subdivisiones, setSubdivisiones] = useState([]);
  // Tags detectados por el matcher del backend (readonly en frontend)
  const [impactosDetectados, setImpactosDetectados] = useState([]);
  // Sustentos ya persistidos en el servidor
  const [sustentos, setSustentos] = useState([]);
  // Sustentos pendientes de subir (creación) — se envían tras el POST
  const [pendingSustentos, setPendingSustentos] = useState([]);
  // Estado de carga durante upload inmediato (edit mode)
  const [uploadingSustento, setUploadingSustento] = useState(false);
  // Preview modal
  const [previewSustento, setPreviewSustento] = useState(null);

  const [form, setForm] = useState(blankForm());
  const [originalLeccion, setOriginalLeccion] = useState(null);
  const [errors, setErrors] = useState({});
  const { user } = useAuth();

  // Segregación de funciones por rol
  const { canView, canCreate, canEdit, canDelete, ready: permsReady } =
    usePermissions();
  const MENU_RUTA = '/constructora/calidad/lecciones-aprendidas';
  const canViewLA = canView(MENU_RUTA);
  const canCreateLA = canCreate(MENU_RUTA);
  const canEditLA = canEdit(MENU_RUTA);
  const canDeleteLA = canDelete(MENU_RUTA);

  // Redirige fuera del detalle si el rol no tiene los permisos necesarios.
  // Esperamos a que el cache de rutas esté listo antes de evaluar.
  useEffect(() => {
    if (!permsReady) return;
    if (!canViewLA) {
      toast.error('No tienes permisos para ver Lecciones Aprendidas');
      navigate(basePath, { replace: true });
      return;
    }
    if (isNew && !canCreateLA) {
      toast.error('No tienes permisos para crear lecciones aprendidas');
      navigate(basePath, { replace: true });
      return;
    }
    if (!isNew && mode === 'edit' && !canEditLA) {
      toast.error('No tienes permisos para editar lecciones aprendidas');
      setMode('view');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permsReady, canViewLA, canCreateLA, canEditLA, isNew, mode]);

  // Precarga del responsable con el usuario logueado (solo para nuevas lecciones,
  // sin sobrescribir si ya tiene valor)
  useEffect(() => {
    if (isNew && user?.nombre && !form.responsable_nombre) {
      setForm((f) => ({
        ...f,
        responsable_nombre: user.nombre,
        responsable_user_id: user.id || null,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, user]);

  // Cargar catálogos
  useEffect(() => {
    (async () => {
      try {
        const [rp, re, res] = await Promise.all([
          proyectosAPI.getAll().catch(() => ({ data: [] })),
          parametrosAPI.getValores('etapa_deteccion_la').catch(() => ({ data: [] })),
          parametrosAPI.getValores('especialidad_la').catch(() => ({ data: [] })),
        ]);
        const proyData = Array.isArray(rp.data) ? rp.data : rp.data?.items || [];
        setProyectos(proyData.filter((p) => p.activo !== false));
        setEtapasDeteccion(Array.isArray(re.data) ? re.data : re.data?.valores || []);
        setEspecialidades(Array.isArray(res.data) ? res.data : res.data?.valores || []);
      } catch {
        toast.error('Error al cargar catálogos');
      }

      if (!isNew) {
        try {
          const r = await leccionesAprendidasAPI.get(id);
          const d = r.data;
          setOriginalLeccion(d);
          setImpactosDetectados(d.impactos || []);
          setSustentos(d.sustentos || []);
          setForm({
            proyecto_id: d.proyecto_id || '',
            aplica_a_todas: !!d.aplica_a_todas,
            etapa_codigo: d.etapa_codigo,
            fecha_identificacion: d.fecha_identificacion,
            responsable_nombre: d.responsable_nombre || '',
            responsable_user_id: d.responsable_user_id,
            etapa_deteccion: d.etapa_deteccion || '',
            especialidad: d.especialidad || '',
            lugar_ejes: d.lugar_ejes || '',
            descripcion_situacion: d.descripcion_situacion || '',
            problema_identificado: d.problema_identificado || '',
            causa: d.causa || '',
            recomendacion: d.recomendacion || '',
            narrativa_impactos: d.narrativa_impactos || '',
            acciones: (d.acciones || []).map((a) => ({
              id: a.id,
              descripcion: a.descripcion,
            })),
          });
        } catch {
          toast.error('No se pudo cargar la lección');
          navigate(basePath);
          return;
        }
      }
      setLoading(false);
      // Restablecer modo según URL al cambiar de id
      if (!isNew) setMode(editParam ? 'edit' : 'view');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Cargar subdivisiones (etapas) del proyecto seleccionado.
  // Si la lección es global o no hay proyecto: limpiar subdivisiones.
  useEffect(() => {
    if (form.aplica_a_todas || !form.proyecto_id) {
      setSubdivisiones([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await subdivisionesAPI.getByProyecto(form.proyecto_id);
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        const etapas = list.filter(
          (s) => (s.tipo_subdivision || '').toLowerCase() === 'etapa' && s.activo !== false
        );
        setSubdivisiones(etapas);
        // Si la etapa actual no pertenece al proyecto, resetear
        const codigoActual = form.etapa_codigo;
        if (codigoActual && !etapas.some((e) => e.codigo === codigoActual)) {
          setForm((f) => ({ ...f, etapa_codigo: null }));
        }
      } catch {
        if (!cancelled) setSubdivisiones([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.proyecto_id, form.aplica_a_todas]);

  const setF = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const setAccion = (idx, v) => {
    setForm((f) => {
      const arr = [...f.acciones];
      arr[idx] = { ...arr[idx], descripcion: v };
      return { ...f, acciones: arr };
    });
  };  const addAccion = () =>
    setForm((f) => ({ ...f, acciones: [...f.acciones, { descripcion: '' }] }));

  const removeAccion = (idx) =>
    setForm((f) => ({
      ...f,
      acciones: f.acciones.length > 1
        ? f.acciones.filter((_, i) => i !== idx)
        : f.acciones,
    }));

  // ---- Sustentos ----
  const SUSTENTO_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
  const SUSTENTO_MIMES = new Set([
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
  ]);
  const SUSTENTO_MAX_BYTES = 10 * 1024 * 1024;

  const _validateFile = (file) => {
    if (!SUSTENTO_MIMES.has(file.type)) {
      return 'Tipo no permitido. Solo PDF, Word o imágenes (jpg/png).';
    }
    if (file.size > SUSTENTO_MAX_BYTES) {
      return 'El archivo excede 10 MB.';
    }
    return null;
  };

  const handlePickFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    // Validar
    const validFiles = [];
    for (const f of files) {
      const err = _validateFile(f);
      if (err) {
        toast.error(`${f.name}: ${err}`);
        continue;
      }
      validFiles.push(f);
    }
    if (!validFiles.length) return;

    if (isNew) {
      // Buffer local: se suben tras el POST de creación
      setPendingSustentos((prev) => [
        ...prev,
        ...validFiles.map((f) => ({
          _local: true,
          _uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file: f,
          filename: f.name,
          mime_type: f.type,
          size_bytes: f.size,
        })),
      ]);
    } else {
      // Upload inmediato con spinner
      setUploadingSustento(true);
      try {
        for (const f of validFiles) {
          try {
            const r = await leccionesAprendidasAPI.uploadSustento(id, f);
            setSustentos((prev) => [...prev, r.data]);
            toast.success(`Sustento adjuntado: ${f.name}`);
          } catch (err) {
            toast.error(
              `Error al adjuntar ${f.name}: ${err.response?.data?.detail || err.message}`
            );
          }
        }
      } finally {
        setUploadingSustento(false);
      }
    }
  };

  const handleRemovePendingSustento = (uid) => {
    setPendingSustentos((prev) => prev.filter((p) => p._uid !== uid));
  };

  const handleDeleteSustento = async (sustentoId) => {
    if (!window.confirm('¿Eliminar este sustento?')) return;
    try {
      await leccionesAprendidasAPI.deleteSustento(id, sustentoId);
      setSustentos((prev) => prev.filter((s) => s.id !== sustentoId));
      toast.success('Sustento eliminado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const validate = () => {
    const e = {};
    if (!form.aplica_a_todas && !form.proyecto_id)
      e.proyecto_id = 'Selecciona un proyecto (o marca "aplica a todas las obras")';
    if (
      !form.aplica_a_todas &&
      form.proyecto_id &&
      subdivisiones.length > 0 &&
      !form.etapa_codigo
    )
      e.etapa_codigo = 'Selecciona una etapa del proyecto';
    if (!form.fecha_identificacion)
      e.fecha_identificacion = 'Fecha obligatoria';
    REQ_TEXT_FIELDS.forEach((k) => {
      if (!String(form[k] || '').trim()) e[k] = 'Obligatorio';
    });
    const narr = String(form.narrativa_impactos || '').trim();
    if (narr.length < 20)
      e.narrativa_impactos =
        'Narrativa de impactos obligatoria (mínimo 20 caracteres)';
    const accEmpty = (form.acciones || []).some(
      (a) => !String(a.descripcion).trim()
    );
    if (!form.acciones.length || accEmpty)
      e.acciones = 'Cada acción requiere descripción';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error('Revisa los campos obligatorios');
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      narrativa_impactos: form.narrativa_impactos.trim(),
      acciones: form.acciones.map((a) => ({
        descripcion: a.descripcion.trim(),
      })),
    };
    try {
      if (isNew) {
        const r = await leccionesAprendidasAPI.create(payload);
        const newId = r.data.id;
        toast.success(`Lección ${r.data.codigo} creada`);
        // Upload síncrono de sustentos pendientes en paralelo
        if (pendingSustentos.length > 0) {
          const results = await Promise.allSettled(
            pendingSustentos.map((p) =>
              leccionesAprendidasAPI.uploadSustento(newId, p.file)
            )
          );
          const fallidos = results
            .map((res, i) => (res.status === 'rejected' ? pendingSustentos[i].filename : null))
            .filter(Boolean);
          if (fallidos.length > 0) {
            toast.error(
              `${fallidos.length} archivo(s) fallaron al adjuntar: ${fallidos.join(', ')}. Puedes reintentar desde el detalle.`
            );
          } else {
            toast.success(`${pendingSustentos.length} sustento(s) adjuntados`);
          }
          setPendingSustentos([]);
        }
        navigate(`${basePath}/${newId}`);
      } else {
        const r = await leccionesAprendidasAPI.update(id, payload);
        setOriginalLeccion(r.data);
        setImpactosDetectados(r.data.impactos || []);
        toast.success('Lección actualizada');
        setMode('view');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') toast.error(detail);
      else if (Array.isArray(detail))
        toast.error(detail.map((d) => d.msg).join(' · '));
      else toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        `¿Eliminar lección ${originalLeccion?.codigo}? Esta acción no se puede deshacer.`
      )
    )
      return;
    try {
      await leccionesAprendidasAPI.delete(id);
      toast.success('Lección eliminada');
      navigate(basePath);
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleMarcarLeida = async () => {
    setMarking(true);
    try {
      const r = await leccionesAprendidasAPI.marcarLeida(id);
      setOriginalLeccion((o) =>
        o
          ? {
              ...o,
              total_lectores: r.data.total_lectores,
              marcada_leida_por_mi: true,
            }
          : o
      );
      toast.success('Marcada como leída');
    } catch {
      toast.error('No se pudo marcar como leída');
    } finally {
      setMarking(false);
    }
  };

  const handleCancel = () => {
    if (isNew) navigate(basePath);
    else {
      // Restaurar form desde original
      if (originalLeccion) {
        const d = originalLeccion;
        setForm({
          proyecto_id: d.proyecto_id || '',
          aplica_a_todas: !!d.aplica_a_todas,
          etapa_codigo: d.etapa_codigo,
          fecha_identificacion: d.fecha_identificacion,
          responsable_nombre: d.responsable_nombre || '',
          responsable_user_id: d.responsable_user_id,
          etapa_deteccion: d.etapa_deteccion || '',
          especialidad: d.especialidad || '',
          lugar_ejes: d.lugar_ejes || '',
          descripcion_situacion: d.descripcion_situacion || '',
          problema_identificado: d.problema_identificado || '',
          causa: d.causa || '',
          recomendacion: d.recomendacion || '',
          narrativa_impactos: d.narrativa_impactos || '',
          acciones: (d.acciones || []).map((a) => ({
            id: a.id,
            descripcion: a.descripcion,
          })),
        });
      }
      setErrors({});
      setMode('view');
    }
  };

  const proyectoNombre = useMemo(() => {
    if (!form.proyecto_id) return '';
    const p = proyectos.find((p) => p.id === form.proyecto_id);
    return p ? `${p.codigo_proyecto || ''}${p.codigo_proyecto ? ' · ' : ''}${p.nombre}` : '';
  }, [proyectos, form.proyecto_id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-[#9d8272]" />
      </div>
    );
  }

  const readOnly = mode === 'view';

  return (
    <div className="space-y-6 max-w-5xl" data-testid="leccion-detalle-page">
      {/* Header */}
      <div className="sticky top-0 z-20 -mx-3 sm:-mx-4 lg:-mx-6 xl:-mx-8 px-3 sm:px-4 lg:px-6 xl:px-8 py-3 bg-[#FAF7F0]/95 backdrop-blur-sm border-b border-[#e8dfd0] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(basePath)}
            data-testid="back-btn"
          >
            <ArrowLeft size={16} className="mr-1" /> Volver
          </Button>
          <div>
            <h1
              className="text-2xl sm:text-3xl font-bold text-[#2D2D2D]"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {isNew ? 'Nueva Lección Aprendida' : originalLeccion?.codigo}
            </h1>
            {!isNew && (
              <div className="flex items-center gap-2 mt-1 text-xs text-[#888888]">
                <span>Creada {fmt(originalLeccion?.created_at)}</span>
                <span>·</span>
                <span>
                  {originalLeccion?.total_lectores} lectores
                </span>
                {originalLeccion?.marcada_leida_por_mi && (
                  <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-[10px]">
                    <CheckCircle2 size={10} className="mr-1" /> Leída por mí
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!isNew && readOnly && (
            <>
              {canEditLA && (
                <Button
                  variant="outline"
                  onClick={() => setMode('edit')}
                  data-testid="edit-mode-btn"
                >
                  <Edit size={14} className="mr-2" /> Editar
                </Button>
              )}
              {canDeleteLA && (
                <Button
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={handleDelete}
                  data-testid="delete-leccion-btn"
                >
                  <Trash2 size={14} className="mr-2" /> Eliminar
                </Button>
              )}
            </>
          )}
          {!readOnly && (
            <>
              <Button
                variant="ghost"
                onClick={handleCancel}
                data-testid="cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
                data-testid="save-leccion-btn"
              >
                {saving ? (
                  <Loader2 size={14} className="mr-2 animate-spin" />
                ) : (
                  <Save size={14} className="mr-2" />
                )}
                {isNew ? 'Crear lección' : 'Guardar cambios'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Aviso obligatoriedad */}
      {!readOnly && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-xs text-amber-900 flex items-center gap-2"
          data-testid="obligatorio-banner"
        >
          <AlertTriangle size={14} />
          <span>
            Todos los campos son obligatorios. Debe registrar al menos 1
            impacto y 1 acción.
          </span>
        </div>
      )}

      {/* Datos generales */}
      <Section title="Datos generales" icon={<BookOpen size={16} />}>
        <Grid>
          <Field label="Proyecto" required error={errors.proyecto_id}>
            {readOnly ? (
              <ReadValue>
                {form.aplica_a_todas
                  ? 'TODAS LAS OBRAS'
                  : originalLeccion?.etapa_nombre
                    ? `${originalLeccion?.proyecto_nombre || '—'} · ${originalLeccion.etapa_nombre}`
                    : (originalLeccion?.proyecto_nombre || '—')}
              </ReadValue>
            ) : (
              <div className="space-y-2">
                <Select
                  value={form.proyecto_id}
                  onValueChange={(v) => setF('proyecto_id', v)}
                  disabled={form.aplica_a_todas}
                >
                  <SelectTrigger
                    className={`h-9 ${form.aplica_a_todas ? 'opacity-50' : ''}`}
                    data-testid="field-proyecto"
                  >
                    <SelectValue placeholder="Selecciona un proyecto" />
                  </SelectTrigger>
                  <SelectContent>
                    {proyectos.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.codigo_proyecto ? `${p.codigo_proyecto} · ` : ''}
                        {p.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label
                  className="flex items-center gap-2 text-xs text-[#5A5A5A] cursor-pointer select-none"
                  data-testid="field-aplica-todas-label"
                >
                  <input
                    type="checkbox"
                    checked={form.aplica_a_todas}
                    onChange={(e) => {
                      setF('aplica_a_todas', e.target.checked);
                      if (e.target.checked) {
                        setF('proyecto_id', '');
                        setF('etapa_codigo', null);
                      }
                    }}
                    className="rounded border-[#c5b8a2] text-[#9d8272] focus:ring-[#9d8272]"
                    data-testid="field-aplica-todas"
                  />
                  Aplica a <strong>todas las obras</strong> (lección global)
                </label>
              </div>
            )}
          </Field>

          {!form.aplica_a_todas && !readOnly && (
            <Field
              label="Etapa del proyecto"
              required={subdivisiones.length > 0}
              error={errors.etapa_codigo}
            >
              {!form.proyecto_id ? (
                <Select disabled value="">
                  <SelectTrigger
                    className="h-9 opacity-50"
                    data-testid="field-etapa-proyecto"
                  >
                    <SelectValue placeholder="Selecciona primero un proyecto" />
                  </SelectTrigger>
                </Select>
              ) : subdivisiones.length === 0 ? (
                <div
                  className="h-9 px-3 flex items-center text-xs italic text-[#8a7864] bg-[#fbf7f0] border border-dashed border-[#d8c9b2] rounded-md"
                  data-testid="field-etapa-proyecto-empty"
                >
                  Este proyecto no tiene etapas registradas
                </div>
              ) : (
                <Select
                  value={form.etapa_codigo || ''}
                  onValueChange={(v) => setF('etapa_codigo', v || null)}
                >
                  <SelectTrigger
                    className="h-9"
                    data-testid="field-etapa-proyecto"
                  >
                    <SelectValue placeholder="Selecciona una etapa" />
                  </SelectTrigger>
                  <SelectContent>
                    {subdivisiones.map((s) => (
                      <SelectItem key={s.id} value={s.codigo}>
                        {s.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          )}

          <Field
            label="Fecha de identificación"
            required
            error={errors.fecha_identificacion}
          >
            {readOnly ? (
              <ReadValue>{fmt(form.fecha_identificacion)}</ReadValue>
            ) : (
              <Input
                type="date"
                className="h-9"
                value={form.fecha_identificacion || ''}
                onChange={(e) => setF('fecha_identificacion', e.target.value)}
                data-testid="field-fecha"
              />
            )}
          </Field>

          <Field
            label="Responsable"
            required
            error={errors.responsable_nombre}
          >
            {readOnly ? (
              <ReadValue>{form.responsable_nombre}</ReadValue>
            ) : (
              <Input
                className="h-9"
                placeholder="Nombre completo"
                value={form.responsable_nombre}
                onChange={(e) => setF('responsable_nombre', e.target.value)}
                data-testid="field-responsable"
              />
            )}
          </Field>

          <Field
            label="Etapa de detección"
            required
            error={errors.etapa_deteccion}
          >
            {readOnly ? (
              <ReadValue>{form.etapa_deteccion}</ReadValue>
            ) : (
              <Select
                value={form.etapa_deteccion}
                onValueChange={(v) => setF('etapa_deteccion', v)}
              >
                <SelectTrigger className="h-9" data-testid="field-etapa-deteccion">
                  <SelectValue placeholder="Selecciona" />
                </SelectTrigger>
                <SelectContent>
                  {etapasDeteccion.map((v) => (
                    <SelectItem key={v.id || v.valor} value={v.valor}>
                      {v.valor}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field
            label="Especialidad"
            required
            error={errors.especialidad}
          >
            {readOnly ? (
              <ReadValue>{form.especialidad}</ReadValue>
            ) : (
              <Select
                value={form.especialidad}
                onValueChange={(v) => setF('especialidad', v)}
              >
                <SelectTrigger className="h-9" data-testid="field-especialidad">
                  <SelectValue placeholder="Selecciona" />
                </SelectTrigger>
                <SelectContent>
                  {especialidades.map((v) => (
                    <SelectItem key={v.id || v.valor} value={v.valor}>
                      {v.valor}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label="Lugar / Ejes" required error={errors.lugar_ejes}>
            {readOnly ? (
              <ReadValue>{form.lugar_ejes}</ReadValue>
            ) : (
              <Input
                className="h-9"
                placeholder="Ej.: Sótano 1, ejes B-3 a C-4"
                value={form.lugar_ejes}
                onChange={(e) => setF('lugar_ejes', e.target.value)}
                data-testid="field-lugar-ejes"
              />
            )}
          </Field>
        </Grid>
      </Section>

      {/* Narrativa */}
      <Section title="Descripción de la lección">
        <div className="grid grid-cols-1 gap-4">
          <Field
            label="Descripción de la situación"
            required
            error={errors.descripcion_situacion}
          >
            {readOnly ? (
              <ReadParagraph>{form.descripcion_situacion}</ReadParagraph>
            ) : (
              <Textarea
                rows={3}
                value={form.descripcion_situacion}
                onChange={(e) => setF('descripcion_situacion', e.target.value)}
                data-testid="field-descripcion-situacion"
              />
            )}
          </Field>
          <Field
            label="Problema identificado"
            required
            error={errors.problema_identificado}
          >
            {readOnly ? (
              <ReadParagraph>{form.problema_identificado}</ReadParagraph>
            ) : (
              <Textarea
                rows={3}
                value={form.problema_identificado}
                onChange={(e) => setF('problema_identificado', e.target.value)}
                data-testid="field-problema"
              />
            )}
          </Field>
          <Field label="Causa" required error={errors.causa}>
            {readOnly ? (
              <ReadParagraph>{form.causa}</ReadParagraph>
            ) : (
              <Textarea
                rows={3}
                value={form.causa}
                onChange={(e) => setF('causa', e.target.value)}
                data-testid="field-causa"
              />
            )}
          </Field>

          {/* Sustentos (adjuntos) — opcional */}
          <SustentosField
            readOnly={readOnly}
            sustentos={sustentos}
            pendingSustentos={pendingSustentos}
            accept={SUSTENTO_ACCEPT}
            uploading={uploadingSustento}
            onPickFiles={handlePickFiles}
            onRemovePending={handleRemovePendingSustento}
            onDelete={handleDeleteSustento}
            onPreview={setPreviewSustento}
          />
        </div>
      </Section>

      {/* Impactos */}
      <Section
        title="Impactos"
        subtitle="Narra los impactos identificados. El sistema los clasificará automáticamente en Costo, Tiempo, Calidad o Riesgo."
      >
        <div className="space-y-3" data-testid="impactos-narrativa-wrapper">
          <Field
            label="Narrativa de impactos"
            required
            error={errors.narrativa_impactos}
          >
            {readOnly ? (
              <ReadParagraph>{form.narrativa_impactos}</ReadParagraph>
            ) : (
              <Textarea
                rows={6}
                placeholder="Describe los impactos observados: retrabajos, sobrecostos, retrasos, calidad, seguridad, etc."
                value={form.narrativa_impactos}
                onChange={(e) => setF('narrativa_impactos', e.target.value)}
                data-testid="field-narrativa-impactos"
              />
            )}
          </Field>

          {impactosDetectados.length > 0 && (
            <div data-testid="impactos-detectados-panel">
              <p className="text-[10px] text-[#9d8272] uppercase tracking-wider mb-2">
                Tipos de impacto detectados
              </p>
              <div className="flex flex-wrap gap-2">
                {impactosDetectados.map((it, idx) => (
                  <Badge
                    key={it.id || idx}
                    variant="outline"
                    className="bg-white text-[#5a4a3a] border-[#c5b8a4]"
                    title={it.descripcion}
                    data-testid={`impacto-detectado-${(it.tipo_impacto || '').toLowerCase()}`}
                  >
                    {it.tipo_impacto}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* Acciones */}
      <Section
        title="Acciones"
        subtitle="Mínimo una. Plan concreto que se implementa para evitar reincidencia."
      >
        <div className="space-y-2" data-testid="acciones-list">
          {form.acciones.map((acc, idx) => (
            <div
              key={idx}
              className="grid grid-cols-12 gap-2 items-start bg-[#fafaf8] border border-[#E8E8E7] rounded-lg p-2"
              data-testid={`accion-row-${idx}`}
            >
              <div className="col-span-1 sm:col-span-1 flex items-center justify-center pt-2">
                <span className="text-xs text-[#888888] font-mono">
                  {idx + 1}.
                </span>
              </div>
              <div className="col-span-10 sm:col-span-10">
                {readOnly ? (
                  <ReadParagraph>{acc.descripcion}</ReadParagraph>
                ) : (
                  <Textarea
                    rows={2}
                    placeholder="Describe la acción…"
                    value={acc.descripcion}
                    onChange={(e) => setAccion(idx, e.target.value)}
                    data-testid={`accion-descripcion-${idx}`}
                  />
                )}
              </div>
              {!readOnly && (
                <div className="col-span-1 flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={form.acciones.length === 1}
                    className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-30"
                    onClick={() => removeAccion(idx)}
                    data-testid={`accion-remove-${idx}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              )}
            </div>
          ))}
          {errors.acciones && (
            <p className="text-xs text-red-600">{errors.acciones}</p>
          )}
          {!readOnly && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addAccion}
              data-testid="accion-add-btn"
            >
              <Plus size={14} className="mr-1" /> Agregar acción
            </Button>
          )}
        </div>
      </Section>

      {/* Recomendación (sección propia al final) */}
      <Section title="Recomendación">
        <Field
          label="Recomendación"
          required
          error={errors.recomendacion}
        >
          {readOnly ? (
            <ReadParagraph>{form.recomendacion}</ReadParagraph>
          ) : (
            <Textarea
              rows={3}
              value={form.recomendacion}
              onChange={(e) => setF('recomendacion', e.target.value)}
              data-testid="field-recomendacion"
            />
          )}
        </Field>

        {/* Panel de confirmación de lectura al final de la lección */}
        {!isNew && readOnly && !originalLeccion?.marcada_leida_por_mi && (
          <div
            className="mt-6 border-t border-[#E8E8E7] pt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            data-testid="marcar-leida-panel"
          >
            <div className="text-sm text-[#5A5A5A] sm:pr-4">
              ¿Ya leíste toda la lección? Márcala como leída para que quede
              registrada tu lectura.
            </div>
            <Button
              onClick={handleMarcarLeida}
              disabled={marking}
              className="bg-[#9d8272] hover:bg-[#8a7162] text-white shrink-0"
              data-testid="marcar-leida-btn"
            >
              {marking ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : (
                <Eye size={14} className="mr-2" />
              )}
              Marcar como leída
            </Button>
          </div>
        )}
      </Section>

      {/* Footer info (solo view) */}
      {readOnly && originalLeccion && (
        <div
          className="bg-[#fafaf8] border border-[#E8E8E7] rounded-lg p-4 text-xs text-[#888888]"
          data-testid="leccion-footer-info"
        >
          <div>Creada: {fmt(originalLeccion.created_at)}</div>
          <div>Última actualización: {fmt(originalLeccion.updated_at)}</div>
          <div>
            Lectores: <strong>{originalLeccion.total_lectores}</strong>
          </div>
          {proyectoNombre && <div>Proyecto: {proyectoNombre}</div>}
        </div>
      )}

      {/* Preview modal para sustentos */}
      {previewSustento && (
        <SustentoPreviewModal
          sustento={previewSustento}
          onClose={() => setPreviewSustento(null)}
        />
      )}
    </div>
  );
};

// ---------- Helper components ----------
const Section = ({ title, subtitle, icon, children }) => (
  <div className="bg-white border border-[#E8E8E7] rounded-lg shadow-sm">
    <div className="px-5 py-3 border-b border-[#E8E8E7] flex items-center gap-2">
      {icon && <span className="text-[#9d8272]">{icon}</span>}
      <div>
        <h3 className="text-base font-semibold text-[#2D2D2D]">{title}</h3>
        {subtitle && (
          <p className="text-xs text-[#888888] mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
);

const Field = ({ label, required, error, children }) => (
  <div>
    <Label className="text-xs text-[#5A5A5A] mb-1 inline-flex items-center gap-1">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

const ReadValue = ({ children }) => (
  <div className="px-3 py-2 bg-[#fafaf8] border border-[#E8E8E7] rounded text-sm text-[#2D2D2D]">
    {children || '—'}
  </div>
);

const ReadParagraph = ({ children }) => (
  <div className="px-3 py-2 bg-[#fafaf8] border border-[#E8E8E7] rounded text-sm text-[#2D2D2D] whitespace-pre-wrap leading-relaxed">
    {children || '—'}
  </div>
);

// -------------------- Sustentos --------------------

const _formatBytes = (b) => {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const _iconForMime = (mime) => {
  if ((mime || '').startsWith('image/'))
    return <FileImage size={16} className="text-[#9d8272]" />;
  return <FileText size={16} className="text-[#9d8272]" />;
};

const SustentosField = ({
  readOnly, sustentos, pendingSustentos, accept, uploading,
  onPickFiles, onRemovePending, onDelete, onPreview,
}) => {
  const inputRef = React.useRef(null);
  const total = (sustentos?.length || 0) + (pendingSustentos?.length || 0);

  return (
    <div data-testid="sustentos-field">
      <Label className="text-xs text-[#5A5A5A] mb-1 inline-flex items-center gap-1">
        Sustentos <span className="text-[#888]">(opcional)</span>
      </Label>
      <p className="text-[11px] text-[#888] mb-2">
        Adjunta archivos PDF, Word o imágenes (JPG/PNG) — máx. 10 MB c/u.
      </p>

      {!readOnly && (
        <div className="mb-3">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple
            className="hidden"
            data-testid="sustento-file-input"
            onChange={(e) => {
              onPickFiles(e.target.files);
              e.target.value = ''; // reset para permitir re-selección
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            data-testid="sustento-upload-btn"
          >
            {uploading ? (
              <>
                <Loader2 size={14} className="mr-1 animate-spin" data-testid="adjuntar-spinner" />
                Subiendo…
              </>
            ) : (
              <>
                <Paperclip size={14} className="mr-1" /> Adjuntar archivo(s)
              </>
            )}
          </Button>
        </div>
      )}

      {total === 0 ? (
        <p className="text-xs text-[#888]">Sin archivos adjuntos.</p>
      ) : (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
          data-testid="sustentos-list"
        >
          {sustentos.map((s) => (
            <SustentoTile
              key={s.id}
              sustento={s}
              readOnly={readOnly}
              onPreview={() => onPreview(s)}
              onDelete={() => onDelete(s.id)}
              testIdPrefix={`sustento-item-${s.id}`}
              previewTestId={`sustento-preview-btn-${s.id}`}
              deleteTestId={`sustento-delete-btn-${s.id}`}
            />
          ))}
          {pendingSustentos.map((p) => (
            <SustentoTile
              key={p._uid}
              sustento={{
                filename: p.filename,
                mime_type: p.mime_type,
                size_bytes: p.size_bytes,
                _localFile: p.file,
              }}
              readOnly={readOnly}
              pending
              onPreview={null}  // buffer local no permite preview server
              onDelete={() => onRemovePending(p._uid)}
              testIdPrefix={`sustento-pending-${p._uid}`}
              deleteTestId={`sustento-remove-pending-${p._uid}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Tile individual (120x120) con thumbnail o ícono, hover-lupa y trash siempre visible
const SustentoTile = ({
  sustento, readOnly, pending,
  onPreview, onDelete, testIdPrefix, previewTestId, deleteTestId,
}) => {
  const mime = sustento.mime_type || '';
  const isImage = mime.startsWith('image/');
  const isPdf = mime === 'application/pdf';
  const isWord =
    mime === 'application/msword' ||
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

  // Para buffer local, generar object URL para el thumbnail de imágenes
  const [localUrl, setLocalUrl] = React.useState(null);
  React.useEffect(() => {
    if (pending && isImage && sustento._localFile) {
      const url = URL.createObjectURL(sustento._localFile);
      setLocalUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [pending, isImage, sustento._localFile]);

  const imgSrc = pending ? localUrl : sustento.file_url;

  const kindLabel = isPdf ? 'PDF' : isWord ? 'DOC' : isImage ? 'IMG' : 'FILE';
  const kindColor = isPdf
    ? 'bg-red-50 text-red-600'
    : isWord
      ? 'bg-blue-50 text-blue-600'
      : 'bg-[#f8f3ec] text-[#9d8272]';

  return (
    <div className="flex flex-col" data-testid={testIdPrefix}>
      <div
        className={`group relative w-[120px] h-[120px] rounded-lg border ${
          pending ? 'border-[#f0e5d0] bg-[#fef8ef]' : 'border-[#E8E8E7] bg-white'
        } overflow-hidden`}
      >
        {/* Thumbnail o ícono */}
        {isImage && imgSrc ? (
          <img
            src={imgSrc}
            alt={sustento.filename}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className={`w-full h-full flex flex-col items-center justify-center ${kindColor}`}>
            <FileText size={38} strokeWidth={1.5} />
            <span className="mt-1 text-[10px] font-semibold tracking-wider">
              {kindLabel}
            </span>
          </div>
        )}

        {/* Overlay con lupa (solo si onPreview) */}
        {onPreview && (
          <button
            type="button"
            onClick={onPreview}
            data-testid={previewTestId}
            className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[1px] opacity-0 group-hover:opacity-100 transition-opacity"
            title="Previsualizar"
          >
            <span className="h-9 w-9 rounded-full bg-white/95 text-[#2D2D2D] flex items-center justify-center shadow-md">
              <Eye size={16} />
            </span>
          </button>
        )}

        {/* Botón eliminar siempre visible (en edit mode) */}
        {!readOnly && (
          <button
            type="button"
            onClick={onDelete}
            data-testid={deleteTestId}
            title={pending ? 'Quitar' : 'Eliminar'}
            className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/95 text-red-600 hover:bg-red-50 flex items-center justify-center shadow-sm"
          >
            {pending ? <X size={12} /> : <Trash2 size={12} />}
          </button>
        )}

        {/* Badge "pending" arriba a la izquierda */}
        {pending && (
          <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-[#f0e5d0] text-[9px] text-[#a08752] font-medium">
            PENDIENTE
          </div>
        )}
      </div>
      <p
        className="mt-1.5 text-[11px] text-[#2D2D2D] truncate w-[120px]"
        title={sustento.filename}
      >
        {sustento.filename}
      </p>
      <p className="text-[10px] text-[#888]">{_formatBytes(sustento.size_bytes)}</p>
    </div>
  );
};

const SustentoPreviewModal = ({ sustento, onClose }) => {
  const mime = sustento.mime_type || '';
  const isImage = mime.startsWith('image/');
  const isPdf = mime === 'application/pdf';
  const isWord =
    mime === 'application/msword' ||
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      data-testid="sustento-preview-modal"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E8E8E7]">
          <div className="flex items-center gap-2 min-w-0">
            {_iconForMime(mime)}
            <p className="text-sm font-medium text-[#2D2D2D] truncate">
              {sustento.filename}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <a
              href={sustento.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-[#5a4a3a] hover:bg-[#f8f3ec] rounded-md"
              data-testid="sustento-download-link"
            >
              <Download size={14} /> Descargar
            </a>
            <Button
              type="button" variant="ghost" size="sm"
              className="h-8 w-8 p-0"
              onClick={onClose}
              data-testid="sustento-preview-close"
            >
              <X size={16} />
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-[#fafaf8]">
          {isImage && (
            <img
              src={sustento.file_url}
              alt={sustento.filename}
              className="max-w-full max-h-[80vh] mx-auto my-4"
            />
          )}
          {isPdf && (
            <iframe
              src={sustento.file_url}
              title={sustento.filename}
              className="w-full h-[80vh] border-0"
            />
          )}
          {isWord && (
            <div className="p-8 text-center text-sm text-[#5A5A5A]">
              <FileText size={48} className="mx-auto text-[#9d8272] mb-4" />
              <p>
                La vista previa de documentos Word no está disponible en el navegador.
              </p>
              <p className="mt-2">
                Usa <strong>Descargar</strong> para abrir el archivo en tu equipo.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeccionDetalle;
